import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { sectionRenderer } from '@theme/section-renderer';

let cartRequest;
let adding = false;
const disclosure = new Map();
const settledCarts = new WeakMap();
const readCart = (fresh = false) => {
  if (fresh) cartRequest = undefined;
  if (!cartRequest) {
    const request = fetch(Theme.routes.cart_url + '.js', { cache: 'no-store' })
      .then(response => {
        if (!response.ok) throw new Error('Cart unavailable');
        return response.json();
      })
      .finally(() => { if (cartRequest === request) cartRequest = undefined; });
    cartRequest = request;
  }
  return cartRequest;
};
const settledCart = event => {
  if (!settledCarts.has(event)) {
    settledCarts.set(event, Promise.resolve(event.promise).then(() => readCart(true)));
  }
  return settledCarts.get(event);
};

export function shippingState(cart, country) {
  if (!cart.item_count || !cart.requires_shipping) return { mode: 'hidden', progress: 0, remaining: 0 };
  if (cart.currency !== 'AUD' || country !== 'AU') return { mode: 'international', progress: 0, remaining: 0 };
  const applications = [...(cart.cart_level_discount_applications || []), ...(cart.discount_applications || [])];
  const shippingCode = (cart.discount_codes || []).some(code => code.applicable === true && code.code.toUpperCase() === 'THANKYOU');
  if (shippingCode || applications.some(discount => discount.target_type === 'shipping_line')) {
    return { mode: 'code', progress: 0, remaining: 0 };
  }
  const total = Math.max(0, Number(cart.total_price) || 0);
  return { mode: total >= 30000 ? 'reached' : 'below', progress: Math.min(30000, total), remaining: Math.max(0, 30000 - total) };
}

class PnoyCartExtras extends Component {
  #listeners;
  #revision = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    for (const name of [StandardEvents.cartLinesUpdate, StandardEvents.cartDiscountUpdate]) {
      document.addEventListener(name, this.#cartChanged, { signal: this.#listeners.signal });
    }
    document.addEventListener(ThemeEvents.cartSectionRestored, this.#refreshAfterRestore, { signal: this.#listeners.signal });
    this.#restoreDisclosure();
    this.#refresh();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#restoreDisclosure();
    this.#refresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#revision++;
  }

  rememberDisclosure() {
    if (this.refs.addonsDetails) disclosure.set(this.dataset.sectionId, this.refs.addonsDetails.open);
  }

  #restoreDisclosure() {
    const details = this.refs.addonsDetails;
    if (details && disclosure.has(this.dataset.sectionId)) details.open = disclosure.get(this.dataset.sectionId);
  }

  #refreshAfterRestore = () => { this.#refresh(); };

  #cartChanged = async event => {
    const revision = ++this.#revision;
    try {
      const cart = await settledCart(event);
      if (this.isConnected && revision === this.#revision) this.#renderCart(cart);
    } catch {}
  };

  async #refresh() {
    const revision = ++this.#revision;
    try {
      const cart = await readCart();
      if (this.isConnected && revision === this.#revision) this.#renderCart(cart);
    } catch {}
  }

  #renderCart(cart) {
    const state = shippingState(cart, this.dataset.country);
    const message = this.refs.shippingMessage;
    const progress = this.refs.shippingProgress;
    if (message) {
      message.parentElement.hidden = state.mode === 'hidden';
      const amount = 'A' + new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', currencyDisplay: 'narrowSymbol' }).format(state.remaining / 100);
      this.refs.shippingText.textContent = (this.dataset[state.mode] || '').replace('__amount__', amount);
      this.refs.shippingCaption.hidden = !['below', 'reached'].includes(state.mode);
      this.refs.shippingCaption.textContent = state.mode === 'reached' ? this.dataset.reachedCaption : this.dataset.caption;
      this.refs.shippingHeading.textContent = state.mode === 'international' ? this.dataset.otherHeading : this.dataset.heading;
    }
    if (progress) {
      progress.hidden = !['below', 'reached'].includes(state.mode);
      progress.value = state.progress;
    }
    const owned = new Set((cart.items || []).map(item => String(item.product_id)));
    for (const row of this.querySelectorAll('[data-product-id]')) row.hidden = owned.has(row.dataset.productId);
    if (this.refs.addonsDetails) {
      this.refs.addonsDetails.hidden = !this.querySelector('[data-product-id]:not([hidden])');
    }
  }

  async addPack(event) {
    const button = event.target.closest('button[data-variant-id]');
    const row = button?.closest('[data-product-id]');
    if (!row || adding || document.querySelector('.cart-items-disabled')) return;
    adding = true;
    for (const control of document.querySelectorAll('pnoy-cart-extras button')) control.disabled = true;
    const feedback = this.refs.feedback;
    feedback.textContent = '';
    let posted = false;
    let deferred;
    const quantity = Number(button.dataset.quantity) === 2 ? 2 : 1;
    try {
      const cart = await readCart();
      if (cart.items.some(item => String(item.product_id) === row.dataset.productId)) {
        this.#renderCart(cart);
        feedback.textContent = this.dataset.owned;
        return;
      }
      const productResponse = await fetch(window.Shopify.routes.root + 'products/' + row.dataset.handle + '.js', { cache: 'no-store' });
      if (!productResponse.ok) throw new Error('Product unavailable');
      const product = await productResponse.json();
      const variant = product.variants.find(item => String(item.id) === button.dataset.variantId);
      if (!variant?.available) {
        row.hidden = true;
        feedback.textContent = this.dataset.unavailable;
        return;
      }
      if (Number(button.dataset.price) !== variant.price) {
        feedback.textContent = this.dataset.priceChanged;
        if (this.dataset.sectionId) {
          await sectionRenderer.renderSection(this.dataset.sectionId, { cache: false, mode: this.closest('theme-drawer') ? 'hydration' : 'full' });
        }
        return;
      }
      const sections = [...new Set([...document.querySelectorAll('cart-items-component')].map(item => item.dataset.sectionId).filter(Boolean))].slice(0, 5);
      deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(new CartLinesUpdateEvent({
        action: 'add',
        context: this.closest('dialog[open]') ? 'dialog' : 'cart',
        lines: [{ merchandiseId: String(variant.id), quantity }],
        promise: deferred.promise,
        detail: { source: 'pnoy-cart-extras' },
      }));
      posted = true;
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variant.id, quantity }], sections, sections_url: window.location.pathname }),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error('Add unavailable');
      row.hidden = true;
      feedback.textContent = this.dataset.added;
      const latest = await readCart(true);
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(latest),
        detail: { source: 'pnoy-cart-extras', itemCount: quantity, items: latest.items, sections: data.sections, didError: false },
      });
      this.#renderCart(latest);
      requestAnimationFrame(() => {
        const active = document.querySelector('dialog[open] pnoy-cart-extras') || document.querySelector('pnoy-cart-extras');
        const target = active?.querySelector('summary') || active?.querySelector('[ref="shippingMessage"]');
        if (target) { target.tabIndex = -1; target.focus({ preventScroll: true }); }
      });
    } catch (error) {
      feedback.textContent = this.dataset.error;
      if (posted) {
        try {
          const latest = await readCart(true);
          this.#renderCart(latest);
          deferred?.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(latest), detail: { source: 'pnoy-cart-extras', didError: true } });
        } catch { button.dataset.uncertain = 'true'; deferred?.reject(error); }
      }
    } finally {
      adding = false;
      for (const control of document.querySelectorAll('pnoy-cart-extras button')) control.disabled = control.dataset.uncertain === 'true';
    }
  }
}

if (!customElements.get('pnoy-cart-extras')) customElements.define('pnoy-cart-extras', PnoyCartExtras);
